import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FormProvider, useForm, yupResolver } from 'FrontRoyalReactHookForm';
import { useAngularContext } from 'AngularContext';
import { Button } from 'Button';
import { Input } from 'Input';
import { Text } from 'Text';
import { showToast } from 'Toast';
import { type AuthClient, type ChangePasswordFormValues } from '../editAccount.types';

// Matches the old change_password directive's input constraint: 8–255 chars, at least one
// non-whitespace character.
const PASSWORD_PATTERN = /^(?=.*\S).{8,255}$/;

type Props = {
    // "Change Password" on desktop, "Save Changes" on mobile (the account form already has a
    // "Save Changes" button on desktop, so the label differs to avoid two identical buttons).
    submitLabel: string;
};

// React port of the embedded change-password directive. Persists via AuthClient.updatePassword
// (an auth service, not Iguana).
export function ChangePassword({ submitLabel }: Props) {
    const { t } = useTranslation('back_royal');
    const $injector = useAngularContext();
    const [submitting, setSubmitting] = useState(false);

    const schema = Yup.object({
        password: Yup.string().required().matches(PASSWORD_PATTERN),
        password_confirmation: Yup.string().required().matches(PASSWORD_PATTERN),
    });

    const methods = useForm<ChangePasswordFormValues>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: { password: '', password_confirmation: '' },
    });
    const { isValid, errors } = methods.formState;

    const updatePassword = async (form: ChangePasswordFormValues) => {
        const authClient = $injector?.get<AuthClient>('AuthClient');
        if (!authClient) return;

        setSubmitting(true);
        try {
            await authClient.updatePassword(form);
            showToast({ message: t('settings.change_password.password_updated'), type: 'success' });
            methods.reset({ password: '', password_confirmation: '' });
        } catch {
            showToast({ message: t('settings.change_password.try_again'), type: 'danger' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(updatePassword)} className="flex max-w-md flex-col gap-4 py-6">
                <Text as="h2" color="black" weight="semibold" className="text-base">
                    {t('settings.change_password.change_password')}
                </Text>
                <Input
                    name="password"
                    type="password"
                    label={t('settings.change_password.password')}
                    isInvalid={!!errors.password}
                />
                <Input
                    name="password_confirmation"
                    type="password"
                    label={t('settings.change_password.re_type')}
                    isInvalid={!!errors.password_confirmation}
                />
                <Button type="submit" color="primary" height={9} fullWidth isDisabled={!isValid || submitting}>
                    {submitLabel}
                </Button>
            </form>
        </FormProvider>
    );
}
